import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Languages, Loader2, Check } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TranslateButton - Automatic translation button using GPT-5.2
 * 
 * @param {Object} sourceTexts - Object with Portuguese texts to translate { field1: "text", field2: "text", ... }
 * @param {Function} onTranslated - Callback that receives translated texts { field1_en: "translated", ... }
 * @param {string} targetLanguage - Target language (default: 'en')
 * @param {string} variant - Button variant
 */
const TranslateButton = ({ 
  sourceTexts = {}, 
  onTranslated, 
  targetLanguage = 'en',
  variant = 'outline',
  size = 'sm'
}) => {
  const { toast } = useToast();
  const [translating, setTranslating] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleTranslate = async () => {
    // Validate that we have texts to translate
    const textsToTranslate = Object.values(sourceTexts).filter(text => text && text.trim());
    
    if (textsToTranslate.length === 0) {
      toast({
        variant: "destructive",
        title: "Nenhum texto para traduzir",
        description: "Preencha os campos em Português primeiro"
      });
      return;
    }

    setTranslating(true);
    setSuccess(false);

    try {
      // Translate each field
      const translatedFields = {};
      
      for (const [fieldName, text] of Object.entries(sourceTexts)) {
        if (!text || !text.trim()) {
          translatedFields[fieldName] = '';
          continue;
        }

        const response = await axios.post(`${API_URL}/api/translate`, {
          text: text,
          target_language: targetLanguage
        });

        translatedFields[fieldName] = response.data.translated_text || text;
      }

      // Call the callback with translated texts
      if (onTranslated) {
        onTranslated(translatedFields);
      }

      setSuccess(true);
      toast({
        title: "Tradução completa!",
        description: `${Object.keys(translatedFields).length} campos traduzidos com GPT-5.2`
      });

      // Reset success indicator after 3 seconds
      setTimeout(() => setSuccess(false), 3000);

    } catch (error) {
      console.error('Translation error:', error);
      toast({
        variant: "destructive",
        title: "Erro na tradução",
        description: error.response?.data?.detail || "Falha ao traduzir. Tente novamente."
      });
    } finally {
      setTranslating(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleTranslate}
      disabled={translating}
      className={`transition-all duration-300 ${
        success 
          ? 'bg-green-50 border-green-500 text-green-700' 
          : 'border-[#00B4D8] text-[#00B4D8] hover:bg-[#E0F2FE]'
      }`}
    >
      {translating ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          A traduzir...
        </>
      ) : success ? (
        <>
          <Check className="w-4 h-4 mr-2" />
          Traduzido!
        </>
      ) : (
        <>
          <Languages className="w-4 h-4 mr-2" />
          Traduzir Automaticamente
        </>
      )}
    </Button>
  );
};

export default TranslateButton;
